import React from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Hammer, Sofa, Drill, Home as HomeIcon, ShieldCheck,
  CheckCircle2, X, ArrowRight, Wrench, Paintbrush, Boxes,
} from "lucide-react";
import useSeo from "@/lib/useSeo";

const groups = [
  {
    title: "Residential Cleaning",
    icon: Sparkles,
    color: "var(--brand-gold)",
    items: [
      "Standard recurring cleaning (Weekly / Bi-weekly / Monthly)",
      "Deep clean — baseboards, inside cabinets, full appliance interiors",
      "Move-in / move-out cleans",
      "Add-ons: laundry, fridge, oven, interior windows, pet homes",
    ],
  },
  {
    title: "Property Maintenance",
    icon: ShieldCheck,
    color: "var(--brand-teal)",
    items: [
      "Quarterly home health-check punch lists",
      "Seasonal exterior touch-ups & gutter cleaning",
      "Pressure-washing & driveway cleaning",
      "Door tuning, hardware tightening, weatherstripping",
    ],
  },
  {
    title: "Handyman & Repairs",
    icon: Hammer,
    color: "var(--brand-gold)",
    items: [
      "Drywall patches & hole repair",
      "Trim, baseboards & crown molding",
      "Door knob, hinge, lockset replacement",
      "Caulking, grout repair, paint touch-ups",
    ],
  },
  {
    title: "Furniture Assembly",
    icon: Sofa,
    color: "var(--brand-teal)",
    items: [
      "IKEA-style flat-pack assembly",
      "Beds, dressers, desks, shelving units",
      "Office workstations & cubicles",
      "Outdoor furniture & playsets",
    ],
  },
  {
    title: "Fixture Installation",
    icon: Drill,
    color: "var(--brand-gold)",
    items: [
      "Wall-mount TVs, mirrors, art & shelving",
      "Blinds, curtain rods, window treatments",
      "Towel bars, toilet paper holders, hooks",
      "Ceiling fans — replacement only (no new wiring)",
    ],
  },
  {
    title: "Light Home Improvement",
    icon: Paintbrush,
    color: "var(--brand-teal)",
    items: [
      "Single-room paint refresh",
      "Cabinet hardware swaps",
      "Backsplash touch-up & sealing",
      "Smart-home device install (doorbells, thermostats — non-electrical)",
    ],
  },
];

const notOffered = [
  "Plumbing work (pipes, water heaters, drain lines, fixtures requiring new water lines)",
  "Electrical work (rewiring, new outlets, panel changes, anything inside a wall behind power)",
];

export default function ServicesPage() {
  useSeo({
    title: "Services — Handyman, Cleaning & Property Maintenance in Evansville, IN",
    description: "Explore our full catalog: standard & deep residential house cleaning, trusted local handyman services, furniture assembly, minor home repairs, fixture installation, and Evansville property maintenance solutions.",
  });
  return (
    <div data-testid="services-page" className="max-w-7xl mx-auto px-6 lg:px-8 py-16 md:py-24">
      <div className="max-w-3xl">
        <span className="section-eyebrow">Our Services</span>
        <h1 className="heading-display mt-3 text-4xl md:text-6xl">Elite Home Care. <br /> Carefully Specialized.</h1>
        <p className="mt-5 text-lg text-slate-600 leading-relaxed">
          We focus exclusively on what we excel at — so every job earns a 5-star result. Browse our complete service catalog below.
        </p>
      </div>

      {/* IMPORTANT EXCLUSIONS */}
      <div className="mt-12 rounded-2xl border-2 border-[var(--brand-gold)]/60 bg-[var(--brand-gold)]/10 p-6 md:p-8" data-testid="exclusions-banner">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-[var(--brand-teal)] text-white flex items-center justify-center flex-shrink-0">
            <X className="w-6 h-6" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[var(--brand-teal)]" style={{ fontFamily: "Outfit" }}>
              We Do <span className="underline">NOT</span> Offer Plumbing or Electrical Services
            </h2>
            <p className="mt-2 text-slate-700 text-sm leading-relaxed">
              To preserve our 5-star reputation and protect your home, we stay in our lane. For licensed plumbing or electrical, we recommend a certified local specialist.
            </p>
            <ul className="mt-4 space-y-2">
              {notOffered.map((n) => (
                <li key={n} className="flex items-start gap-2 text-sm text-slate-700">
                  <X className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  {n}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* SERVICE GROUPS */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groups.map((g) => (
          <div key={g.title} className="card-elevated p-7 flex flex-col" data-testid={`service-group-${g.title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: `${g.color}22`, color: g.color }}>
              <g.icon className="w-6 h-6" />
            </div>
            <h3 className="text-lg font-bold text-[var(--brand-teal)] mb-3" style={{ fontFamily: "Outfit" }}>{g.title}</h3>
            <ul className="space-y-2.5 flex-1">
              {g.items.map((i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                  <CheckCircle2 className="w-4 h-4 text-[var(--brand-teal)] mt-0.5 flex-shrink-0" />
                  {i}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/quote" className="card-elevated p-7 hover:shadow-xl transition flex items-center justify-between gap-4" data-testid="services-cta-instant">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--brand-gold-deep)]">Cleaning · Within 20 Miles of Evansville</div>
            <div className="text-xl font-extrabold text-[var(--brand-teal)] mt-1" style={{ fontFamily: "Outfit" }}>Get an Instant Booking</div>
            <div className="text-xs text-slate-500 mt-1">Transparent matrix pricing — see your price live. Strictly within the 20-mile radius.</div>
          </div>
          <ArrowRight className="w-5 h-5 text-[var(--brand-teal)]" />
        </Link>
        <Link to="/request-quote" className="card-elevated p-7 hover:shadow-xl transition flex items-center justify-between gap-4 border-[var(--brand-gold)]" data-testid="services-cta-custom" style={{ borderTop: "3px solid var(--brand-gold)" }}>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[var(--brand-gold-deep)]">Everything Else · Tri-State Coverage</div>
            <div className="text-xl font-extrabold text-[var(--brand-teal)] mt-1" style={{ fontFamily: "Outfit" }}>Request a Custom Quote</div>
            <div className="text-xs text-slate-500 mt-1">Handyman, assembly, fixtures, improvements, property management — or cleaning outside the 20-mile radius. Reply in 1 business day.</div>
          </div>
          <ArrowRight className="w-5 h-5 text-[var(--brand-gold-deep)]" />
        </Link>
      </div>
    </div>
  );
}
